import { ConflictMatch, DetectedConflict } from '@/services/conflictService';

/**
 * Match-like object for TBD detection
 * Works with ConflictMatch['match'], AgendaMatch, or any object with dateTime and status
 */
interface MatchLike {
  dateTime?: string | null;
  status?: string;
}

/**
 * Check if a match is effectively TBD (unscheduled)
 * Exported for use across all conflict-related components
 *
 * Note: We rely on status='TBD' or null dateTime. We do NOT check for midnight
 * times as placeholders because midnight could be a legitimate game time in
 * different timezones. The import process properly sets status='TBD' when needed.
 */
export function isMatchTBD(match: MatchLike): boolean {
  // Check status first - this is the source of truth
  if (match.status === 'TBD') {
    return true;
  }
  // Also check for null/undefined/empty dateTime
  if (!match.dateTime) {
    return true;
  }
  return false;
}

/** Match duration assumed by the app (matches are start time + 2 hours) */
const MATCH_DURATION_MINUTES = 120;
/** Warm-up time before kickoff (must match backend WARMUP_TIME_MINUTES) */
const WARMUP_MINUTES = 60;

/**
 * Format time duration in hours and minutes
 * Exported for use in other components
 */
export function formatDuration(minutes: number): string {
  // Round to nearest 5 minutes — these are estimates, and ~50m is easier
  // to reason about than ~51m
  const rounded = Math.round(minutes / 5) * 5;
  if (rounded < 60) {
    return `${rounded || 5}m`;
  }

  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
}

/**
 * Build a travel suffix showing drive time and spare time (or just drive time for DIRECT).
 */
function buildTravelSuffix(travelMinutes: number, windowMinutes: number): string {
  const buffer = windowMinutes - travelMinutes;
  if (buffer > 0) {
    return ` — Est. travel time ${formatDuration(travelMinutes)}, ${formatDuration(buffer)} to spare`;
  }
  // DIRECT: just show travel time — the message already conveys the problem
  return ` — Est. travel time ${formatDuration(travelMinutes)}`;
}

/**
 * Compute ETA string: source match start + match duration + travel time
 */
function computeEtaTime(sourceDateTime: string, travelMinutes: number): string {
  const etaMs = new Date(sourceDateTime).getTime() + (MATCH_DURATION_MINUTES + travelMinutes) * 60000;
  return formatMatchTime(new Date(etaMs).toISOString());
}

/**
 * Get first name from full name
 */
function getFirstName(fullName: string): string {
  return fullName.split(' ')[0];
}

/**
 * Generate a consolidated message for multiple players with TBD matches
 * Use this when displaying TBD conflicts for a date with potentially many players
 */
export function generateConsolidatedTBDMessage(playerNames: string[]): string {
  if (playerNames.length === 0) {
    return '';
  }

  const firstNames = playerNames.map(getFirstName);

  if (firstNames.length === 1) {
    return `${firstNames[0]} has an unscheduled match`;
  }

  if (firstNames.length === 2) {
    return `${firstNames[0]} & ${firstNames[1]} have unscheduled matches`;
  }

  // 3+ players: "A, B & C have unscheduled matches"
  const lastPlayer = firstNames.pop();
  return `${firstNames.join(', ')} & ${lastPlayer} have unscheduled matches`;
}

/**
 * Extract unique player names with TBD matches from a list of conflicts
 * Returns array of full player names (use with generateConsolidatedTBDMessage)
 */
export function extractTBDPlayerNames(conflicts: DetectedConflict[]): string[] {
  const playerNames = new Set<string>();

  conflicts.forEach(conflict => {
    if (conflict.timeConflict.isPotential) {
      if (isMatchTBD(conflict.match1.match)) {
        playerNames.add(conflict.match1.playerName);
      }
      if (isMatchTBD(conflict.match2.match)) {
        playerNames.add(conflict.match2.playerName);
      }
    }
  });

  return Array.from(playerNames);
}

/**
 * Separate TBD conflicts from scheduled conflicts
 * Returns { tbdPlayerNames, scheduledConflicts }
 */
export function separateConflictsByType(conflicts: DetectedConflict[]): {
  tbdPlayerNames: string[];
  scheduledConflicts: DetectedConflict[];
} {
  const tbdPlayerNames = new Set<string>();
  const scheduledConflicts: DetectedConflict[] = [];

  conflicts.forEach(conflict => {
    if (conflict.timeConflict.isPotential) {
      if (isMatchTBD(conflict.match1.match)) {
        tbdPlayerNames.add(conflict.match1.playerName);
      }
      if (isMatchTBD(conflict.match2.match)) {
        tbdPlayerNames.add(conflict.match2.playerName);
      }
    } else {
      scheduledConflicts.push(conflict);
    }
  });

  return {
    tbdPlayerNames: Array.from(tbdPlayerNames),
    scheduledConflicts
  };
}

/**
 * Generate agenda-style conflict message (shorter format for inline display)
 * Used in AgendaDialog and calendar popups
 */
export function generateAgendaConflictMessage(
  conflict: {
    severity: 'DIRECT' | 'MODERATE' | 'MINIMAL';
    timeDifference: number;
    availableTravelWindow?: number;
    travelTime?: { durationSeconds: number };
  },
  player1Name: string,
  player2Name: string,
  match1IsTBD: boolean,
  match2IsTBD: boolean,
  sourceDateTime?: string | null
): string {
  const player1FirstName = getFirstName(player1Name);
  const player2FirstName = getFirstName(player2Name);
  const isSamePlayer = player1Name === player2Name;
  const isPotential = match1IsTBD || match2IsTBD;

  // Handle TBD scenarios
  if (isPotential) {
    if (isSamePlayer) {
      return `${player1FirstName} has an unscheduled match`;
    } else if (match1IsTBD && match2IsTBD) {
      return `${player1FirstName} & ${player2FirstName} have unscheduled matches`;
    } else if (match1IsTBD) {
      return `${player1FirstName} has an unscheduled match`;
    } else {
      return `${player2FirstName} has an unscheduled match`;
    }
  }

  // Handle simultaneous matches
  if (conflict.timeDifference === 0) {
    if (isSamePlayer) {
      return `${player1FirstName} has matches at the same time`;
    } else {
      return `${player1FirstName} & ${player2FirstName} have matches at the same time`;
    }
  }

  // Physical time overlap: next match starts before previous match ends (2hr duration)
  if (conflict.timeDifference > 0 && conflict.timeDifference <= MATCH_DURATION_MINUTES) {
    if (isSamePlayer) {
      return `${player1FirstName} has overlapping matches`;
    } else {
      return `${player1FirstName} & ${player2FirstName} have overlapping matches`;
    }
  }

  // Build travel info suffix
  const hasTravelData = conflict.availableTravelWindow !== undefined && conflict.travelTime?.durationSeconds !== undefined;
  const travelMinutes = hasTravelData ? conflict.travelTime!.durationSeconds / 60 : 0;
  const windowMinutes = hasTravelData ? Math.max(0, conflict.availableTravelWindow!) : 0;
  let travelSuffix = '';
  if (hasTravelData) {
    travelSuffix = buildTravelSuffix(travelMinutes, windowMinutes);
  }

  // With travel data: use actual numbers to decide overlap vs tight
  if (hasTravelData) {
    if (travelMinutes > windowMinutes) {
      // Can't make the trip — distinguish warmup-only vs match impact
      const shortfallMinutes = travelMinutes - windowMinutes;
      const lateToKickoff = shortfallMinutes - WARMUP_MINUTES;
      const eta = sourceDateTime ? computeEtaTime(sourceDateTime, travelMinutes) : null;
      const etaPart = eta ? `, ETA ~${eta}` : '';
      if (lateToKickoff > 0) {
        return `Insufficient travel time between matches${etaPart}${travelSuffix}`;
      } else {
        return `Travel between matches cuts into warm-up${etaPart}${travelSuffix}`;
      }
    } else {
      const bufferMinutes = windowMinutes - travelMinutes;
      if (bufferMinutes <= 60) {
        // Back-to-back — buffer is 1hr or less
        return `Back-to-back matches${travelSuffix}`;
      } else {
        // Tight window — buffer is more than 1hr but still flagged
        return `Tight window${travelSuffix}`;
      }
    }
  }

  // No travel data — overlapping or potential conflict
  if (conflict.severity === 'DIRECT') {
    if (isSamePlayer) {
      return `${player1FirstName} has overlapping matches`;
    } else {
      return `${player1FirstName} & ${player2FirstName} have overlapping matches`;
    }
  }

  // No travel data, not overlapping — we can't say for sure
  if (isSamePlayer) {
    return `${player1FirstName} may have a conflicting match — check travel times`;
  } else {
    return `${player1FirstName} & ${player2FirstName} may have conflicting matches — check travel times`;
  }
}

/**
 * Generate human-readable conflict description with proper timezone handling
 * Used in ConflictSummaryCard and detailed conflict views
 */
export function generateConflictDescription(conflict: DetectedConflict, timeRefs?: MatchTimeRefs): string {
  const { match1, match2, timeConflict } = conflict;
  const isSamePlayer = match1.playerId === match2.playerId;
  const name1 = getFirstName(match1.playerName);
  const name2 = getFirstName(match2.playerName);
  const time1 = getTimeRef(timeRefs, match1.playerId, match1.match.id);
  const time2 = getTimeRef(timeRefs, match2.playerId, match2.match.id);

  // Handle potential conflicts (TBD matches)
  if (timeConflict.isPotential) {
    const isTBD1 = isMatchTBD(match1.match);
    const isTBD2 = isMatchTBD(match2.match);

    if (isTBD1 && isTBD2) {
      if (isSamePlayer) {
        return `${name1} has unscheduled matches in ${match1.leagueName} and ${match2.leagueName}`;
      } else {
        return `${name1} & ${name2} have unscheduled matches`;
      }
    }

    if (isTBD1) {
      return `${name1} has an unscheduled match`;
    } else {
      return `${name2} has an unscheduled match`;
    }
  }

  // Handle simultaneous matches
  if (timeConflict.isSimultaneous || timeConflict.timeDifference === 0) {
    if (isSamePlayer) {
      return `${matchesRef(name1, time1, time2)} are at the same time`;
    } else {
      return `${matchRef(name1, time1)} and ${matchRef(name2, time2)} are at the same time`;
    }
  }

  // Physical time overlap: next match starts before previous match ends (2hr duration)
  if (timeConflict.timeDifference > 0 && timeConflict.timeDifference <= MATCH_DURATION_MINUTES) {
    if (isSamePlayer) {
      return `${matchRef(name1, time1)} overlaps with ${matchRef(name1, time2)}`;
    } else {
      return `${matchRef(name1, time1)} overlaps with ${matchRef(name2, time2)}`;
    }
  }

  // Build travel info suffix
  const hasTravelData = timeConflict.travelTime && timeConflict.availableTravelWindow !== undefined;
  const travelMinutes = hasTravelData ? timeConflict.travelTime!.durationSeconds / 60 : 0;
  const windowMinutes = hasTravelData ? Math.max(0, timeConflict.availableTravelWindow!) : 0;
  let travelSuffix = '';
  if (hasTravelData) {
    travelSuffix = buildTravelSuffix(travelMinutes, windowMinutes);
  }

  // With travel data: use actual numbers to decide overlap vs tight
  if (hasTravelData) {
    if (travelMinutes > windowMinutes) {
      // Can't make the trip — include ETA at destination
      const shortfallMinutes = travelMinutes - windowMinutes;
      const lateToKickoff = shortfallMinutes - WARMUP_MINUTES;
      const eta = match1.match.dateTime ? computeEtaTime(match1.match.dateTime, travelMinutes) : null;
      const etaPart = eta ? `, ETA ~${eta}` : '';
      if (lateToKickoff > 0) {
        if (isSamePlayer) {
          return `Insufficient travel time between ${matchesRef(name1, time1, time2)}${etaPart}${travelSuffix}`;
        } else {
          return `Insufficient travel time between ${matchRef(name1, time1)} and ${matchRef(name2, time2)}${etaPart}${travelSuffix}`;
        }
      } else {
        if (isSamePlayer) {
          if (time1 && time2) {
            return `Travel from ${name1}'s ${time1} match cuts into warm-up for the ${time2} match${etaPart}${travelSuffix}`;
          }
          return `Travel between ${name1}'s matches cuts into warm-up${etaPart}${travelSuffix}`;
        } else {
          return `Travel from ${matchRef(name1, time1)} cuts into warm-up for ${matchRef(name2, time2)}${etaPart}${travelSuffix}`;
        }
      }
    } else {
      const bufferMinutes = windowMinutes - travelMinutes;
      if (bufferMinutes <= 60) {
        // Back-to-back — buffer is 1hr or less
        if (isSamePlayer) {
          return `Back-to-back matches for ${matchesRef(name1, time1, time2)}${travelSuffix}`;
        } else {
          return `Back-to-back matches: ${matchRef(name1, time1)} and ${matchRef(name2, time2)}${travelSuffix}`;
        }
      } else {
        // Tight window — buffer is more than 1hr but still flagged
        if (isSamePlayer) {
          return `Tight window between ${matchesRef(name1, time1, time2)}${travelSuffix}`;
        } else {
          return `Tight window between ${matchRef(name1, time1)} and ${matchRef(name2, time2)}${travelSuffix}`;
        }
      }
    }
  }

  // No travel data — overlapping or potential conflict
  if (timeConflict.severity === 'DIRECT') {
    if (isSamePlayer) {
      return `${matchRef(name1, time1)} overlaps with ${matchRef(name1, time2)}`;
    } else {
      return `${matchRef(name1, time1)} overlaps with ${matchRef(name2, time2)}`;
    }
  }

  // No travel data, not overlapping — we can't say for sure
  if (isSamePlayer) {
    return `${matchesRef(name1, time1, time2)} may conflict — check travel times`;
  } else {
    return `${matchRef(name1, time1)} and ${matchRef(name2, time2)} may conflict — check travel times`;
  }
}

export interface MatchCardConflictInfo {
  /** Short label for the ribbon (e.g. "Overlap", "Back-to-back") */
  label: string;
  /** Full description for the card body */
  text: string;
  travelDetail?: string;
  /** Whether this match is the destination (incoming) or source (outgoing) of the conflict */
  direction?: 'incoming' | 'outgoing';
  /** Icon hint for non-directional conflicts */
  icon?: 'schedule' | 'overlap' | 'tbd' | 'warning';
}

/**
 * Generate conflict description from the perspective of a specific match card.
 * Replaces the current match's reference with "This match" and keeps the other side as-is.
 * Returns text + optional travelDetail for separate-line rendering.
 */
export function generateMatchCardConflictDescription(
  conflict: DetectedConflict,
  currentMatchId: string,
  timeRefs?: MatchTimeRefs
): MatchCardConflictInfo {
  const { match1, match2, timeConflict } = conflict;

  const isMatch1 = match1.match.id === currentMatchId;
  const drawerSide = isMatch1 ? match1 : match2;
  const cardSide = isMatch1 ? match2 : match1;
  // card is source (earlier) when drawer match is match2
  const cardIsSource = !isMatch1;

  // Build a reference to the other match by time, e.g. "the 10:00am match"
  const cardTime = cardSide.match.dateTime
    ? (getTimeRef(timeRefs, cardSide.playerId, cardSide.match.id)
       || formatMatchTime(cardSide.match.dateTime))
    : null;
  const cardFirstName = getFirstName(cardSide.playerName);
  const otherRef = cardTime ? `${cardFirstName}'s ${cardTime} match` : `${cardFirstName}'s match`;

  // Handle potential conflicts (TBD matches)
  if (timeConflict.isPotential) {
    const drawerIsTBD = isMatchTBD(drawerSide.match);
    const cardIsTBD = isMatchTBD(cardSide.match);

    if (drawerIsTBD && cardIsTBD) {
      return { label: 'Unscheduled', text: `Both matches are unscheduled — possible conflict once times are set`, icon: 'tbd' };
    }
    if (cardIsTBD) {
      return { label: 'Unscheduled', text: `This match is unscheduled and may conflict once a time is set`, icon: 'tbd' };
    }
    return { label: 'Unscheduled', text: `Time not yet set — possible conflict once scheduled`, icon: 'tbd' };
  }

  // Handle simultaneous matches
  if (timeConflict.isSimultaneous || timeConflict.timeDifference === 0) {
    return { label: 'Same time', text: `Starts at the same time as ${otherRef}`, icon: 'schedule' };
  }

  // Physical time overlap
  if (timeConflict.timeDifference > 0 && timeConflict.timeDifference <= MATCH_DURATION_MINUTES) {
    return { label: 'Overlap', text: `Overlaps with ${otherRef}`, icon: 'overlap' };
  }

  // Build travel detail string
  const hasTravelData = timeConflict.travelTime && timeConflict.availableTravelWindow !== undefined;
  const travelMinutes = hasTravelData ? timeConflict.travelTime!.durationSeconds / 60 : 0;
  const windowMinutes = hasTravelData ? Math.max(0, timeConflict.availableTravelWindow!) : 0;
  let travelDetail: string | undefined;
  if (hasTravelData) {
    const buffer = windowMinutes - travelMinutes;
    if (buffer > 0) {
      travelDetail = `Est. travel time ${formatDuration(travelMinutes)}, ${formatDuration(buffer)} to spare`;
    } else {
      travelDetail = `Est. travel time ${formatDuration(travelMinutes)}`;
    }
  }

  // With travel data
  if (hasTravelData) {
    // Arrow from drawer's perspective: outgoing (↗) when card is later, incoming (↙) when card is earlier
    const direction = cardIsSource ? 'incoming' as const : 'outgoing' as const;

    // Compute ETA at destination
    const eta = match1.match.dateTime ? computeEtaTime(match1.match.dateTime, travelMinutes) : undefined;
    const etaPart = eta ? `, ETA ~${eta}` : '';

    if (travelMinutes > windowMinutes) {
      // Determine whether the shortfall eats into warmup only or game time too
      const shortfallMinutes = travelMinutes - windowMinutes;
      const affectsGameTime = shortfallMinutes > WARMUP_MINUTES;
      const overlapTarget = affectsGameTime ? 'game time' : 'warm-up';
      // cardIsSource = card is the earlier match; direction tells perspective:
      //   incoming (card is earlier) → "Travel from {other}" on the later match card
      //   outgoing (card is later)   → "Travel to {other}" on the earlier match card
      const dirWord = cardIsSource ? 'from' : 'to';
      return { label: 'Travel', text: `Travel ${dirWord} ${otherRef} overlaps with ${overlapTarget}${etaPart}`, travelDetail, direction };
    } else {
      const bufferMinutes = windowMinutes - travelMinutes;
      if (bufferMinutes < WARMUP_MINUTES) {
        const dirWord = cardIsSource ? 'from' : 'to';
        return { label: 'Warm-up', text: `Travel ${dirWord} ${otherRef} cuts into warm-up time`, travelDetail, direction };
      } else if (bufferMinutes <= WARMUP_MINUTES + 30) {
        return { label: 'Back-to-back', text: `Back-to-back with ${otherRef}`, travelDetail, direction };
      } else {
        return { label: 'Tight', text: `Tight window between matches`, travelDetail, direction };
      }
    }
  }

  // No travel data — overlapping or potential conflict
  if (timeConflict.severity === 'DIRECT') {
    return { label: 'Overlap', text: `Overlaps with ${otherRef}`, icon: 'overlap' };
  }

  return { label: 'Possible', text: `May conflict with ${otherRef} — check travel times`, icon: 'warning' };
}

/**
 * A consolidated conflict merges multiple pairwise DetectedConflicts
 * that involve overlapping sets of players into a single display item.
 */
export interface ConsolidatedConflict {
  id: string;
  description: string;
  severity: 'MINIMAL' | 'MODERATE' | 'DIRECT';
  matches: ConflictMatch[];
  originalConflicts: DetectedConflict[];
}

/**
 * Format a match dateTime as a short time string: "8am", "10:30am", "6pm"
 */
function formatMatchTime(dateTime: string): string {
  const d = new Date(dateTime);
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  if (minutes === 0) return `${hours}${ampm}`;
  return `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;
}

/**
 * Map from playerId → matchId → short time string.
 * Only populated for players with 2+ distinct matches in the conflict set.
 */
export type MatchTimeRefs = Map<string, Map<string, string>>;

/**
 * Build time-based references for each player's matches across a set of conflicts.
 * Players with only 1 match get no entry (their match doesn't need disambiguation).
 */
export function buildMatchTimeRefs(conflicts: DetectedConflict[]): MatchTimeRefs {
  // Collect all matches per player
  const playerMatches = new Map<string, Map<string, string>>(); // playerId → matchId → dateTime

  for (const c of conflicts) {
    for (const cm of [c.match1, c.match2]) {
      if (!playerMatches.has(cm.playerId)) playerMatches.set(cm.playerId, new Map());
      playerMatches.get(cm.playerId)!.set(cm.match.id, cm.match.dateTime);
    }
  }

  const timeRefs: MatchTimeRefs = new Map();

  for (const [playerId, matches] of playerMatches) {
    if (matches.size < 2) continue; // no disambiguation needed for single-match players

    const matchTimeMap = new Map<string, string>();
    for (const [matchId, dateTime] of matches) {
      matchTimeMap.set(matchId, formatMatchTime(dateTime));
    }
    timeRefs.set(playerId, matchTimeMap);
  }

  return timeRefs;
}

/**
 * Get time reference for a player's match, or undefined if not needed.
 */
function getTimeRef(timeRefs: MatchTimeRefs | undefined, playerId: string, matchId: string): string | undefined {
  if (!timeRefs) return undefined;
  const playerTimes = timeRefs.get(playerId);
  if (!playerTimes) return undefined;
  return playerTimes.get(matchId);
}

/**
 * Format a possessive match reference: "Jake's match" or "Jake's 8am match"
 */
function matchRef(name: string, timeRef?: string): string {
  return timeRef ? `${name}'s ${timeRef} match` : `${name}'s match`;
}

/**
 * Format a possessive matches reference for same player:
 * "Jake's matches" or "Jake's 8am and 10am matches"
 */
function matchesRef(name: string, time1?: string, time2?: string): string {
  if (time1 && time2) return `${name}'s ${time1} and ${time2} matches`;
  return `${name}'s matches`;
}

/**
 * Format a list of first names: "Jake", "Jake and Emma", "Jake, Emma and Liam"
 */
function formatNameList(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Categorize a conflict into a bucket key for grouping similar conflicts.
 * Conflicts with the same category can be merged into one display item.
 */
function getConflictCategory(conflict: DetectedConflict): string {
  const { timeConflict } = conflict;
  if (timeConflict.isPotential) return 'TBD';
  if (timeConflict.isSimultaneous || timeConflict.timeDifference === 0) return 'SIMULTANEOUS';

  // Physical time overlap: next match starts before previous match ends (2hr duration)
  if (timeConflict.timeDifference > 0 && timeConflict.timeDifference <= MATCH_DURATION_MINUTES) return 'OVERLAP';

  const hasTravelData = timeConflict.travelTime && timeConflict.availableTravelWindow !== undefined;
  if (hasTravelData) {
    const travelMinutes = timeConflict.travelTime!.durationSeconds / 60;
    const windowMinutes = Math.max(0, timeConflict.availableTravelWindow!);
    return travelMinutes > windowMinutes ? 'TRAVEL_IMPOSSIBLE' : 'TRAVEL_TIGHT';
  }

  if (timeConflict.severity === 'DIRECT') return 'OVERLAP';
  return 'POTENTIAL';
}

/**
 * Generate a merged description for travel conflicts sharing the same destination match.
 * E.g. "Travel from Bro and Snowy's 1pm match will cut into warm-up time for Snowy's 4pm match"
 */
function generateMergedTravelDescription(conflicts: DetectedConflict[], timeRefs: MatchTimeRefs): string {
  // All conflicts share the same destination match (match2.match.id)
  const dest = conflicts[0].match2;
  const destName = getFirstName(dest.playerName);
  const destTime = getTimeRef(timeRefs, dest.playerId, dest.match.id)
    || (dest.match.dateTime ? formatMatchTime(dest.match.dateTime) : undefined);
  const destRef = destTime ? `${destName}'s ${destTime} match` : `${destName}'s match`;

  // Collect unique source players with their match times
  const sourceInfos: { name: string; time?: string }[] = [];
  const seenPlayers = new Set<string>();
  for (const c of conflicts) {
    if (!seenPlayers.has(c.match1.playerId)) {
      seenPlayers.add(c.match1.playerId);
      const time = getTimeRef(timeRefs, c.match1.playerId, c.match1.match.id)
        || (c.match1.match.dateTime ? formatMatchTime(c.match1.match.dateTime) : undefined);
      sourceInfos.push({ name: getFirstName(c.match1.playerName), time });
    }
  }

  // Put destination player last in source list for readability
  // "Bro and Snowy's 1pm match" reads better than "Snowy and Bro's 1pm match"
  // when destination is Snowy
  const destFirstName = destName;
  sourceInfos.sort((a, b) => {
    if (a.name === destFirstName) return 1;
    if (b.name === destFirstName) return -1;
    return 0;
  });

  // Format source reference
  let sourceRef: string;
  const names = sourceInfos.map(s => s.name);
  const times = sourceInfos.map(s => s.time).filter(Boolean) as string[];
  const allHaveTimes = times.length === sourceInfos.length;
  const allSameTime = allHaveTimes && times.every(t => t === times[0]);

  if (allSameTime && times[0]) {
    // Same time: "Bro and Snowy's 1pm match"
    sourceRef = `${formatNameList(names)}'s ${times[0]} match`;
  } else if (allHaveTimes) {
    // Different times: "Bro's 12pm match and Snowy's 1pm match"
    const refs = sourceInfos.map(s => `${s.name}'s ${s.time} match`);
    sourceRef = formatNameList(refs);
  } else {
    // No time info
    sourceRef = `${formatNameList(names)}'s match`;
  }

  // Travel stats (worst buffer)
  let worstTravelMin = 0;
  let worstWindowMin = 0;
  let worstBuffer = Infinity;
  for (const c of conflicts) {
    if (c.timeConflict.travelTime && c.timeConflict.availableTravelWindow !== undefined) {
      const tm = c.timeConflict.travelTime.durationSeconds / 60;
      const wm = Math.max(0, c.timeConflict.availableTravelWindow);
      const buffer = wm - tm;
      if (buffer < worstBuffer) {
        worstBuffer = buffer;
        worstTravelMin = tm;
        worstWindowMin = wm;
      }
    }
  }
  const travelSuffix = buildTravelSuffix(worstTravelMin, worstWindowMin);

  // Physical time overlap trumps travel analysis
  const category = getConflictCategory(conflicts[0]);
  if (category === 'OVERLAP') {
    return `${sourceRef} overlaps with ${destRef}`;
  }
  if (category === 'TRAVEL_IMPOSSIBLE') {
    const worstShortfall = worstTravelMin - worstWindowMin;
    const lateToKickoff = worstShortfall - WARMUP_MINUTES;
    // Compute ETA using worst-case source match
    const sourceDateTime = conflicts.reduce<string | null>((worst, c) => {
      if (!c.match1.match.dateTime) return worst;
      if (!worst) return c.match1.match.dateTime;
      return new Date(c.match1.match.dateTime).getTime() > new Date(worst).getTime()
        ? c.match1.match.dateTime : worst;
    }, null);
    const eta = sourceDateTime ? computeEtaTime(sourceDateTime, worstTravelMin) : null;
    const etaPart = eta ? `, ETA ~${eta}` : '';
    if (lateToKickoff > 0) {
      return `Insufficient travel time between ${sourceRef} and ${destRef}${etaPart}${travelSuffix}`;
    } else {
      return `Travel from ${sourceRef} cuts into warm-up for ${destRef}${etaPart}${travelSuffix}`;
    }
  }
  // TRAVEL_TIGHT
  return `Tight window between ${sourceRef} and ${destRef}${travelSuffix}`;
}

/**
 * Generate a merged conflict for multiple pairwise conflicts sharing the same
 * physical match pair but involving different players.
 * E.g. "Snowy's 8:30am match overlaps with Snowy and Bro's 10am match"
 */
function generateMergedMatchPairConflict(
  group: DetectedConflict[],
  timeRefs: MatchTimeRefs
): ConsolidatedConflict {
  // Collect unique player names for source (match1) and destination (match2)
  const sourceNames: string[] = [];
  const destNames: string[] = [];
  const seenSource = new Set<string>();
  const seenDest = new Set<string>();

  for (const c of group) {
    if (!seenSource.has(c.match1.playerId)) {
      seenSource.add(c.match1.playerId);
      sourceNames.push(getFirstName(c.match1.playerName));
    }
    if (!seenDest.has(c.match2.playerId)) {
      seenDest.add(c.match2.playerId);
      destNames.push(getFirstName(c.match2.playerName));
    }
  }

  // Get time refs from any conflict in the group (they share the same match IDs)
  const first = group[0];
  const sourceTime = getTimeRef(timeRefs, first.match1.playerId, first.match1.match.id)
    || (first.match1.match.dateTime ? formatMatchTime(first.match1.match.dateTime) : undefined);
  const destTime = getTimeRef(timeRefs, first.match2.playerId, first.match2.match.id)
    || (first.match2.match.dateTime ? formatMatchTime(first.match2.match.dateTime) : undefined);

  // Build references: "Snowy's 8:30am match", "Snowy and Bro's 10am match"
  const sourceRef = sourceTime
    ? `${formatNameList(sourceNames)}'s ${sourceTime} match`
    : `${formatNameList(sourceNames)}'s match`;
  const destRef = destTime
    ? `${formatNameList(destNames)}'s ${destTime} match`
    : `${formatNameList(destNames)}'s match`;

  // Pick description based on conflict category
  const cat = getConflictCategory(first);
  let description: string;
  if (cat === 'SIMULTANEOUS') {
    description = `${sourceRef} and ${destRef} are at the same time`;
  } else if (cat === 'OVERLAP') {
    description = `${sourceRef} overlaps with ${destRef}`;
  } else {
    description = `${sourceRef} and ${destRef} may conflict — check travel times`;
  }

  // Collect all unique matches
  const matchMap = new Map<string, ConflictMatch>();
  for (const c of group) {
    matchMap.set(c.match1.match.id + c.match1.playerId, c.match1);
    matchMap.set(c.match2.match.id + c.match2.playerId, c.match2);
  }

  const worstSeverity = group.reduce<'MINIMAL' | 'MODERATE' | 'DIRECT'>(
    (worst, c) => {
      const order = { DIRECT: 2, MODERATE: 1, MINIMAL: 0 } as const;
      return order[c.severity] > order[worst] ? c.severity : worst;
    },
    'MINIMAL'
  );

  return {
    id: group.map(c => c.id).join('-'),
    description,
    severity: worstSeverity,
    matches: Array.from(matchMap.values()),
    originalConflicts: group,
  };
}

/**
 * Consolidate pairwise conflicts into grouped display items.
 * Travel conflicts are grouped by destination match to merge same-player and
 * cross-player conflicts that share the same destination (e.g. two kids both
 * traveling from the same 1pm match to one kid's 4pm match).
 * Non-travel conflicts use same-player/cross-player grouping.
 */
export function consolidateConflicts(conflicts: DetectedConflict[]): ConsolidatedConflict[] {
  // Build time refs across ALL conflicts for this day
  const timeRefs = buildMatchTimeRefs(conflicts);

  if (conflicts.length <= 1) {
    return conflicts.map(c => ({
      id: c.id,
      description: generateConflictDescription(c, timeRefs),
      severity: c.severity,
      matches: [c.match1, c.match2],
      originalConflicts: [c],
    }));
  }

  // Separate travel conflicts (can be merged by destination) from others
  const travelConflicts: DetectedConflict[] = [];
  const otherConflicts: DetectedConflict[] = [];
  for (const c of conflicts) {
    const cat = getConflictCategory(c);
    if (cat === 'TRAVEL_IMPOSSIBLE' || cat === 'TRAVEL_TIGHT') {
      travelConflicts.push(c);
    } else {
      otherConflicts.push(c);
    }
  }

  const result: ConsolidatedConflict[] = [];

  // Travel conflicts: group by destination match + category to merge
  // conflicts that share the same destination (regardless of same/cross player)
  if (travelConflicts.length > 0) {
    const groups = new Map<string, DetectedConflict[]>();
    for (const c of travelConflicts) {
      const cat = getConflictCategory(c);
      const key = `${c.match2.match.id}:${cat}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }

    for (const [, group] of groups) {
      if (group.length === 1) {
        const c = group[0];
        result.push({
          id: c.id,
          description: generateConflictDescription(c, timeRefs),
          severity: c.severity,
          matches: [c.match1, c.match2],
          originalConflicts: [c],
        });
      } else {
        const matchMap = new Map<string, ConflictMatch>();
        for (const c of group) {
          matchMap.set(c.match1.match.id + c.match1.playerId, c.match1);
          matchMap.set(c.match2.match.id + c.match2.playerId, c.match2);
        }
        const worstSeverity = group.reduce<'MINIMAL' | 'MODERATE' | 'DIRECT'>(
          (worst, c) => {
            const order = { DIRECT: 2, MODERATE: 1, MINIMAL: 0 } as const;
            return order[c.severity] > order[worst] ? c.severity : worst;
          },
          'MINIMAL'
        );
        result.push({
          id: group.map(c => c.id).join('-'),
          description: generateMergedTravelDescription(group, timeRefs),
          severity: worstSeverity,
          matches: Array.from(matchMap.values()),
          originalConflicts: group,
        });
      }
    }
  }

  // Non-travel conflicts: group by match pair to consolidate conflicts
  // that involve the same two physical matches but different players
  // (e.g. Snowy's 8:30am overlaps with the 10am match for both Snowy and Bro)
  if (otherConflicts.length > 0) {
    const groups = new Map<string, DetectedConflict[]>();
    for (const c of otherConflicts) {
      const key = `${c.match1.match.id}:${c.match2.match.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }

    for (const [, group] of groups) {
      if (group.length === 1) {
        const c = group[0];
        result.push({
          id: c.id,
          description: generateConflictDescription(c, timeRefs),
          severity: c.severity,
          matches: [c.match1, c.match2],
          originalConflicts: [c],
        });
      } else {
        // Multiple conflicts for the same match pair — merge player names
        result.push(generateMergedMatchPairConflict(group, timeRefs));
      }
    }
  }

  return result;
}
